import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import isEqual from 'lodash/isEqual';
import throttle from 'lodash/throttle';

import { withSearchHandler, withModalStateHandlers } from 'decorators';
import { Button } from 'components/core/Button';
import { ModalClose } from 'components/core/Modal';
import { Paragraph } from 'components/atoms';
import { calculateFilterCount } from 'utils/filterCount';
import './SearchFiltersTray.scss';

const allowedKeys = ['location', 'latitude', 'longitude', 'zip', 'city', 'state', 'subPageType', 'landingPageSize', 'neighborhood'];

export function enhance(Component) {
	function SearchFiltersTrayWithState(props) {
		const [filterParams, setFilterParams] = useState(props.queryParams);
		const [isScrolled, setIsScrolled] = useState(false);
		const prevQueryParamsRef = useRef(props.queryParams);

		useEffect(() => {
			if (!isEqual(prevQueryParamsRef.current, props.queryParams)) {
				setFilterParams(props.queryParams);
				prevQueryParamsRef.current = props.queryParams;
			}
		}, [props.queryParams]);

		useEffect(() => {
			const handleScroll = throttle(() => {
				const scrolled = window.scrollY > 0;
				setIsScrolled((prev) => prev !== scrolled ? scrolled : prev);
			}, 100);
			window.addEventListener('scroll', handleScroll);
			return () => {
				handleScroll.cancel();
				window.removeEventListener('scroll', handleScroll);
			};
		}, []);

		const handleQueryParamChange = useCallback((params) => {
			if (props.isLarge) {
				props.onSearchParamChange(params);
			}
			setFilterParams(params);
		}, [props.isLarge, props.onSearchParamChange]);

		const handleApplyFilters = useCallback(() => {
			if (!props.isLarge && props.closeFilters) {
				props.closeFilters();
			}
			props.onSearchParamChange(filterParams);
		}, [props.isLarge, props.closeFilters, props.onSearchParamChange, filterParams]);

		const handleClearAll = useCallback(() => {
			const newParams = Object.keys(filterParams).reduce(
				(acc, key) => {
					acc[key] = allowedKeys.includes(key) ? filterParams[key] : undefined;
					return acc;
				},
				{},
			);
			newParams.searchType = 'storage';
			props.onSearchParamChange(newParams);
			setFilterParams(newParams);
		}, [filterParams, props.onSearchParamChange]);

		return (
			<Component
				{...props}
				filterParams={filterParams}
				isScrolled={isScrolled}
				handleQueryParamChange={handleQueryParamChange}
				handleApplyFilters={handleApplyFilters}
				handleClearAll={handleClearAll}
			/>
		);
	}

	return withSearchHandler(withModalStateHandlers(SearchFiltersTrayWithState));
}

export function SearchFiltersTray({
	children,
	filtersShowing,
	handleQueryParamChange,
	handleClearAll,
	filterParams,
	handleApplyFilters,
	closeFilters,
	isLarge,
	isScrolled,
	filtersCount // Calculated by parent, in use on mobile due to "apply" button being required to update filters
}) {
	// utilized by desktop clearAll button as it doesn't require the "apply" button to change the filters
	const hasFiltersApplied = calculateFilterCount(filterParams, filterParams?.searchType) > 0;

	return (
		<div
			className={classnames('filters-tray', { showing: filtersShowing, scrolled: isScrolled })}
		>
			{!isLarge && closeFilters && (
				<div className="filters-tray-header">
					<h2 className="filters-tray-title">Filters</h2>
					{filtersCount > 0 && (
						<Button
							type="button"
							className="clearAll"
							link={true}
							gray={true}
							onClick={handleClearAll}
						>
							Clear All
						</Button>
					)}
					<ModalClose
						onClick={closeFilters}
						className="close-button-header"
					/>
				</div>
			)}
			{isLarge && (
				<div className="filter-header">
					<Paragraph
						size="standard"
						color="black"
						weight="ultraBold"
					>
						Filters
					</Paragraph>
					<Button
						type="button"
						className="clearAll"
						disabled={!filtersCount && !hasFiltersApplied}
						link={true}
						onClick={handleClearAll}
					>
						Clear All
					</Button>
				</div>
			)}
			<div className="filters-tray-content">
				{React.cloneElement(children, {
					queryParams: filterParams,
					onFilter: handleQueryParamChange,
				})}
			</div>
			<div className={classnames('apply-all-filters')}>
				<Button onClick={handleApplyFilters}>Apply Filters</Button>
			</div>
		</div>
	);
}

SearchFiltersTray.propTypes = {
	filtersShowing: PropTypes.bool,
	children: PropTypes.node.isRequired,
	filterParams: PropTypes.object,
	handleQueryParamChange: PropTypes.func,
	handleClearAll: PropTypes.func,
	handleApplyFilters: PropTypes.func.isRequired,
	closeFilters: PropTypes.func,
	isLarge: PropTypes.bool,
	isScrolled: PropTypes.bool,
	filtersCount: PropTypes.number,
};

export default enhance(SearchFiltersTray);
